import { Injectable } from '@angular/core';
import { BookingSlotSelectPolicyEnum } from '../../booking/services/booking-slot-select-policy.enum';
import { BookingSlot } from '../../booking/models/booking-slot';
import { Observable, Subscriber } from '../../../../../node_modules/rxjs';
import { ConnectionService } from '../../../services/connection/connection.service';
import { map } from 'rxjs/operators';
import { CalendarService } from '../../general/services/calendar.service';
import { OrderService } from '../../order/services/order.service';
import { DayOfWeek } from '../../booking/models/day-of-week';
import { TimeRestriction } from '../../booking/models/time-restriction';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { Order } from '../../order/models/order';
import { BookingOrderService } from '../../booking/services/booking-order.service';
import { AreaItem } from '../../booking/models/area-item';
import { BookingService } from '../../booking/services/booking.service';
import { LoadingSpinnerService } from '../../../services/loading-spinner.service';
import { environment } from '../../../../environments/environment';

@Injectable()
export class BookingSlotChangeStateService extends CalendarService {

  private _selectedDate: Date;
  private _orderId: number;
  private _allAreaItems: AreaItem[];

  constructor(
    shopSettingsService: ShopSettingsService,
    private connectionService: ConnectionService,
    private orderService: OrderService,
    private bookingOrderService: BookingOrderService,
    private bookingService: BookingService,
    private loadingSpinnerService: LoadingSpinnerService,
  ) {
    super();
    this.shopSettingsService = shopSettingsService;
  }

  private _bookingSlots = new Array<BookingSlot>();

  initialize(orderId: number, selectedDate: Date): Observable<boolean> {
    return Observable.create(
      (observable: Subscriber<boolean>) => {
        this.reset();
        this._orderId = orderId;
        this._selectedDate = selectedDate;
        this.reloadSlots().subscribe(() => {
          this.bookingService.getAllAreaItems().subscribe(
            allAreaItems => {
              this._allAreaItems = allAreaItems;
              this.orderService.getOrderRestrictions(this._orderId).pipe(map(
                restrictions => {
                  this.reloadDisableDays(restrictions);
                  observable.next(true);
                }
              )
              ).subscribe();
            },
            error => {
              // TODO: handle errors
              observable.error();
            },
          );
        });
      }
    );
  }

  reset() {
    this._orderId = 0;
    this._selectedDate = null;
    this._bookingSlots = new Array<BookingSlot>();
    this._allAreaItems = new Array<AreaItem>();
  }

  get bookingSlots(): BookingSlot[] {
    return this._bookingSlots;
  }

  get orderId(): number {
    return this._orderId;
  }

  get isBirthday(): boolean {
    return false;
  }

  reloadSlots(): Observable<boolean> {
    return this._reloadSlots(this.orderId, this.selectedDate);
  }

  private _reloadSlots(orderId: number, selectedDate: Date): Observable<boolean> {
    return Observable.create(
      (observable: Subscriber<boolean>) => {
        this.getOrderSlots(orderId, selectedDate).subscribe(
          bookingSlots => {
            this._bookingSlots = bookingSlots;
          });
        observable.next(true);
      });
  }

  getOrderSlots(orderId: number, date: Date): Observable<BookingSlot[]> {
    date = environment.globalSettings.getIsoDateStrByDate(date);
    return this.connectionService.sendJsonPostRequest('/store/area/order-slots', {
      orderId: orderId,
      bookingDate: date,
    }).pipe(map(
      result => {
        if (!result) {
          return null;
        }

        return result.map(x => BookingSlot.createFromAny(x));
      }
    ));
  }

  get selectedDate(): Date {
    return this._selectedDate;
  }

  dateSelect(
    date: Date,
    bookingSlotSelectPolicy: BookingSlotSelectPolicyEnum = BookingSlotSelectPolicyEnum.FirstSlot,
  ): Observable<boolean> {
    return Observable.create(
      (observable: Subscriber<boolean>) => {
        this._selectedDate = date;
        this.reloadSlots().subscribe(() => {
          observable.next(true);
        });
      }
    );
  }

  shiftDay(offset: number) {
    if (!this._selectedDate || !offset) {
      return;
    }

    const newSelectedDate = this.searchNearEnabledDate(this._selectedDate, 6, offset);

    if (newSelectedDate) {
      const selectPolicy = offset > 0 ? BookingSlotSelectPolicyEnum.FirstSlot : BookingSlotSelectPolicyEnum.LastSlot;
      this.dateSelect(newSelectedDate, selectPolicy);
    }
  }

  get displayTimeSlots(): BookingSlot[] {
    if (this._bookingSlots) {
      return this._bookingSlots.filter(x => x.nextSlotValid);
    }
    return this._bookingSlots;
  }

  searchNearEnabledDate(date, maxDays, offset): Date {
    let counter = 0;
    const newSelectedDate = new Date(date);
    while (counter <= maxDays) {
      newSelectedDate.setDate(newSelectedDate.getDate() + offset);
      if (this.isDateEnabled(newSelectedDate)) {
        const selectPolicy = offset > 0 ? BookingSlotSelectPolicyEnum.FirstSlot : BookingSlotSelectPolicyEnum.LastSlot;
        return newSelectedDate;
      }
      counter++;
    }
    return null;
  }

  validateChangeTimeSlot(order: Order, bookingSlot: BookingSlot): boolean {
    let isChangeTimeSlotValid = true;
    if (!order || !order.orderLines || order.orderLines.length === 0 || !bookingSlot || !this._allAreaItems || this._allAreaItems.length === 0) {
      return false;
    }
    for (const orderLine of order.orderLines) {
      const areaItemTemp = this._allAreaItems.find(a => a.productId === orderLine.productId);
      const productVals = {
        'id': orderLine.productId,
        'price': orderLine.price,
        'qty': orderLine.quantity,
        'productPriceSchedules': areaItemTemp && areaItemTemp.productPriceSchedules ? areaItemTemp.productPriceSchedules : null,
      };
      const price = this.bookingOrderService.computePriceByProductVals(productVals, [bookingSlot]);
      if (price !== orderLine.price) {
        isChangeTimeSlotValid = false;
        break;
      }
    }

    return isChangeTimeSlotValid;
  }

  reloadDisableDays(restrictions: TimeRestriction[]) {
    if (!restrictions) {
      return false;
    }
    const allowedDaysOfWeek = new Map<DayOfWeek, TimeRestriction>();
    restrictions.map(res => {
      allowedDaysOfWeek.set(res.dayOfWeek, res);
    });

    const enabledDaysOfWeek = allowedDaysOfWeek.size ? Array.from(allowedDaysOfWeek.keys()) : Array<number>();

    const disabledDaysOfWeek = [];
    if (enabledDaysOfWeek && enabledDaysOfWeek.length) {
      for (let i = 0; i < 7; i++) {
        if (!(enabledDaysOfWeek.indexOf(i) > -1)) {
          disabledDaysOfWeek.push(i);
        }
      }
    }
    this._disabledDaysOfWeek = disabledDaysOfWeek;

    return true;
  }

}
